import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { LICENCE_STATUSES } from '@siteflow/shared';
import { LicenceStatusChip, humanise } from '@/components/admin/licence-status-chip';
import { DataTable, dataTableColumnHelper } from '@/components/data-table';
import { PageHeader } from '@/components/page-header';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminApi, adminKeys, type AdminLicenceRow } from '@/lib/api/admin';
import { messageForError } from '@/lib/api-client';
import { formatDate } from '@/lib/utils';

const PER_PAGE = 25;
const ALL = 'all';
const col = dataTableColumnHelper<AdminLicenceRow>();

function LicencesPage() {
  const { page, status } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const params = { page, perPage: PER_PAGE, ...(status ? { status } : {}) };
  const query = useQuery({
    queryKey: adminKeys.licences(params),
    queryFn: () => adminApi.licences(params),
    placeholderData: (previous) => previous,
  });

  const columns = [
    col.accessor('organisationName', {
      header: 'Organisation',
      cell: (info) => <span className="font-medium">{info.getValue()}</span>,
    }),
    col.accessor('status', { header: 'Status', cell: (info) => <LicenceStatusChip status={info.getValue()} /> }),
    col.accessor('planKey', { header: 'Plan' }),
    col.accessor('kind', { header: 'Kind', cell: (info) => humanise(info.getValue()) }),
    col.display({
      id: 'slots',
      header: 'Slots',
      cell: ({ row }) => `${row.original.slotsUsed} / ${row.original.slotsTotal ?? '∞'}`,
    }),
    col.accessor('expiresAt', { header: 'Expires', cell: (info) => formatDate(info.getValue()) }),
    col.accessor('lastValidatedAt', {
      header: 'Last validated',
      cell: (info) => formatDate(info.getValue()),
    }),
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Platform"
        title="Licences"
        description="Every licence on the platform. Open one to extend it, grant slots, or change its state."
      />

      <div className="w-56">
        <Select
          value={status ?? ALL}
          onValueChange={(value) =>
            void navigate({
              search: { page: 1, status: value === ALL ? undefined : (value as typeof LICENCE_STATUSES[number]) },
            })
          }
        >
          <SelectTrigger aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {LICENCE_STATUSES.map((value) => (
              <SelectItem key={value} value={value}>
                {humanise(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        caption="Licences"
        columns={columns}
        data={query.data?.items ?? []}
        isLoading={query.isPending}
        error={query.isError ? messageForError(query.error) : undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) =>
          void navigate({ to: '/admin/licences/$licenceId', params: { licenceId: row.id } })
        }
        pagination={{
          page,
          perPage: PER_PAGE,
          ...(query.data ? { total: query.data.total } : {}),
          onPageChange: (next) => void navigate({ search: { page: next, status } }),
        }}
      />
    </div>
  );
}

export const Route = createFileRoute('/admin/licences/')({
  validateSearch: z.object({
    page: z.coerce.number().int().min(1).catch(1).default(1),
    status: z.enum(LICENCE_STATUSES).optional().catch(undefined),
  }),
  component: LicencesPage,
});
